import { App, Button, Skeleton, Space, Switch } from "antd"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { DndProvider, useDrag, useDrop } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import { useLocation, useNavigate } from "react-router"
import { Link } from "react-router-dom"
import { SampleSelector } from "../components"
import { getOrders } from "../endpoints"
import { getPlate, savePlate } from "../endpoints/plateEndpoints"
import type { OrderInfoInterface, SampleInterface } from "../types"

const ROWS = 8
const COLS = 12
const WELL_LABELS = Array.from({ length: ROWS }, (_, i) => String.fromCharCode("A".charCodeAt(0) + i))

function getPlateLayout(samples: SampleInterface[], fillByColumn: boolean): (SampleInterface | null)[] {
    const wells: (SampleInterface | null)[] = Array.from({ length: ROWS * COLS }, () => null)
    if (fillByColumn) {
        samples.forEach((sample, i) => {
            if (i >= ROWS * COLS) return // Prevent out of bounds
            const col = Math.floor(i / ROWS)
            const row = i % ROWS
            const idx = col * ROWS + row
            if (idx < wells.length) wells[idx] = sample
        })
    } else {
        // Row fill: fill by rows, but convert to column-major indexing for consistency
        samples.forEach((sample, i) => {
            if (i >= ROWS * COLS) return // Prevent out of bounds
            const row = Math.floor(i / COLS)
            const col = i % COLS
            const idx = col * ROWS + row
            if (idx < wells.length) wells[idx] = sample
        })
    }
    return wells
}

function getDefaultFillByColumn(orders: OrderInfoInterface[]): boolean {
    const submissionTypes = orders
        .map((order) => order.submission_type ?? order.plateOrder)
        .filter(Boolean)

    if (submissionTypes.length === 0) {
        return true
    }

    const hasRowOrder = submissionTypes.some(
        (submissionType) => submissionType === "plate-row-order" || submissionType === "row"
    )
    const hasColumnOrder = submissionTypes.some(
        (submissionType) => submissionType === "plate-column-order" || submissionType === "column"
    )

    if (hasRowOrder && !hasColumnOrder) {
        return false
    }

    return true
}

const ItemType = "ORDER_ID"
function DraggableOrderId({
    id,
    index,
    moveOrderId,
}: {
    id: number
    index: number
    moveOrderId: (from: number, to: number) => void
}) {
    const ref = useRef<HTMLSpanElement>(null)
    const [, drop] = useDrop({
        accept: ItemType,
        hover(item: { index: number }) {
            if (!ref.current) return
            if (item.index === index) return
            moveOrderId(item.index, index)
            item.index = index
        },
    })
    const [{ isDragging }, drag] = useDrag({
        type: ItemType,
        item: { id, index },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    })
    drag(drop(ref))
    return (
        <span
            ref={ref}
            style={{
                opacity: isDragging ? 0.5 : 1,
                cursor: "move",
                padding: "0.3em 0.8em",
                border: "1px solid #1890ff",
                borderRadius: 4,
                background: "#fff",
                color: "#1890ff",
                fontWeight: 500,
                marginLeft: "1em",
                userSelect: "none",
                display: "inline-block",
            }}
        >
            <Link to={`/admin/order/${id}`} style={{ color: "#1890ff" }}>
                {id}
            </Link>
        </span>
    )
}

export function PlateEditor(): JSX.Element {
    const { modal } = App.useApp()
    const location = useLocation()
    const navigate = useNavigate()
    const fillPreferenceInitializedRef = useRef(false)
    const initialOrderIds: number[] = (location.state?.orderIds || []).slice().sort((a: number, b: number) => a - b)
    const [orderIds, setOrderIds] = useState<number[]>(initialOrderIds)
    const [fillByColumn, setFillByColumn] = useState(true)
    const [selectByColumn, setSelectByColumn] = useState(true)
    //wells state will hold the samples in the plate layout
    // it will be an array of 96 SampleInterface or null for empty wells
    // e.g. [sample1, sample2, null, sample3, ...]
    // this will be used to render the plate layout
    // and to handle user interactions like copy/paste/delete
    // userEdited tracks if the user has manually modified wells
    // to prevent auto-filling when they change samples or fillByColumn
    // if userEdited is true, we won't auto-fill wells
    // if userEdited is false, we will auto-fill wells based on samples and fillByColumn
    const [wells, setWells] = useState<(SampleInterface | null)[]>([])
    const [userEdited, setUserEdited] = useState(false)
    const [samples, setSamples] = useState<SampleInterface[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<Error | null>(null)
    const [selectedWells, setSelectedWells] = useState<number[]>([])
    const [clipboardSamples, setClipboardSamples] = useState<(SampleInterface | null)[]>([])
    const [clipboardPattern, setClipboardPattern] = useState<'row' | 'column' | 'rectangle' | 'auto'>('auto') // Track the pattern of copied cells
    const [clipboardShape, setClipboardShape] = useState<{width: number, height: number} | null>(null) // Track shape for rectangular patterns
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [undoStack, setUndoStack] = useState<(SampleInterface | null)[][]>([])
    const [editingWellIdx, setEditingWellIdx] = useState<number | null>(null)
    const [editingValue, setEditingValue] = useState<string>("") //current value of the input field
    const [editingOrderId, setEditingOrderId] = useState<string>("")
    const [orders, setOrders] = useState<OrderInfoInterface[]>([])
    const [comparePlateNumber, setComparePlateNumber] = useState<string>("")
    const [compareLoading, setCompareLoading] = useState(false)
    const [compareResult, setCompareResult] = useState<{
        differentPosition: { sampleName: string; currentPos: string; otherPos: string }[]
        notInOther: string[]
    } | null>(null)
    // Fetch orders and samples
    useEffect(() => {
        let isMounted = true
        setLoading(true)
        setError(null)
        ;(async () => {
            try {
                const res = await getOrders({ type: "list", orderIds })
                if (!isMounted) return
                const resorders: OrderInfoInterface[] = res.orders || []
                let sortedOrders = resorders.sort((a, b) => orderIds.indexOf(a.id ?? 0) - orderIds.indexOf(b.id ?? 0))
                sortedOrders = sortedOrders.map((order) => {
                    return {
                        ...order,
                        samples: order.samples.map((sample) => {
                            return { ...sample, selected: true }
                        }),
                    }
                })
                setOrders(sortedOrders)
                if (!fillPreferenceInitializedRef.current) {
                    setFillByColumn(getDefaultFillByColumn(sortedOrders))
                    fillPreferenceInitializedRef.current = true
                }

                setLoading(false)
            } catch (err: any) {
                if (!isMounted) return
                setError(err instanceof Error ? err : new Error(String(err)))
                setLoading(false)
            }
        })()
        return () => {
            isMounted = false
        }
    }, [orderIds])
    useEffect(() => {
        // console.log("orders changed", orders)
        const allSamples: SampleInterface[] = orders.flatMap(
            (order) => order.samples.filter((sample) => sample.selected) || []
        )
        //sort allSamples first by the order in orderIds then by id
        allSamples.sort((a, b) => {
            const orderA = orders.findIndex((order) => order.samples.some((s) => s.id === a.id))
            const orderB = orders.findIndex((order) => order.samples.some((s) => s.id === b.id))
            if (orderA !== orderB) return orderA - orderB
            return (a.id ?? 0) - (b.id ?? 0)
        })
        setSamples(allSamples)
    }, [orders])

    // Auto-fill wells when samples or fillByColumn changes, unless user has edited
    useEffect(() => {
        if (!userEdited) {
            // console.log('samples are',samples)
            setWells(getPlateLayout(samples, fillByColumn))
        }
    }, [samples, fillByColumn, userEdited])

    // Helper to push current wells to undo stack before a mutating operation
    const pushUndo = useCallback(() => {
        setUndoStack((prev) => [...prev, wells.map((s) => (s ? { ...s } : null))])
    }, [wells])

    const moveOrderId = (from: number, to: number) => {
        if (from === to) return
        const updated = [...orderIds]
        const [removed] = updated.splice(from, 1)
        updated.splice(to, 0, removed)
        setOrderIds(updated)
    }

    const handleEditSave = (idx: number) => {
        const orderIdValue = editingOrderId.trim()
        if (!orderIdValue) {
            modal.error({ content: "Order ID is required." })
            return
        }
        const orderIdNumber = Number(orderIdValue)
        if (Number.isNaN(orderIdNumber)) {
            modal.error({ content: "Order ID must be a number." })
            return
        }
        const samplenumValue = editingValue.trim()
        if (!samplenumValue) {
            modal.error({ content: "Samplenum is required." })
            return
        }
        const samplenumNumber = Number(samplenumValue)
        if (Number.isNaN(samplenumNumber)) {
            modal.error({ content: "Samplenum must be a number." })
            return
        }
        const matchedOrder = orders.find((order) => order.id === orderIdNumber)
        const matchedSample = matchedOrder?.samples.find((sample) => sample.samplenum === samplenumNumber)
        if (!matchedSample) {
            modal.error({
                content: `No sample found for order ${orderIdNumber} with samplenum ${samplenumNumber}.`,
            })
            return
        }
        setWells((prev) => {
            const updated = [...prev]
            updated[idx] = { ...matchedSample }
            return updated
        })
        setEditingWellIdx(null)
        setEditingValue("")
        setEditingOrderId("")
        setUserEdited(true)
    }
    const handleWellClick = useCallback(
        (idx: number, e: React.MouseEvent | React.KeyboardEvent) => {
            if (e.shiftKey && selectedWells.length > 0) {
                // Range select
                if (!selectByColumn) {
                    // Row order: select row-wise from start to end position
                    const last = selectedWells[selectedWells.length - 1]
                    const startCol = Math.floor(last / ROWS)
                    const endCol = Math.floor(idx / ROWS)
                    const startRow = last % ROWS
                    const endRow = idx % ROWS
                    const range: number[] = []
                    
                    // Determine the row-wise order between start and end positions
                    const startPos = startRow * COLS + startCol
                    const endPos = endRow * COLS + endCol
                    const [minPos, maxPos] = [Math.min(startPos, endPos), Math.max(startPos, endPos)]
                    
                    // Convert back to well indices, selecting row by row
                    for (let pos = minPos; pos <= maxPos; pos++) {
                        const row = Math.floor(pos / COLS)
                        const col = pos % COLS
                        if (row < ROWS && col < COLS) {
                            range.push(col * ROWS + row)
                        }
                    }
                    setSelectedWells(Array.from(new Set([...selectedWells, ...range])))
                } else {
                    // Column range select
                    const last = selectedWells[selectedWells.length - 1]
                    const startCol = Math.floor(last / ROWS)
                    const endCol = Math.floor(idx / ROWS)
                    const startRow = last % ROWS
                    const endRow = idx % ROWS
                    const range: number[] = []
                    for (let col = Math.min(startCol, endCol); col <= Math.max(startCol, endCol); col++) {
                        for (let row = Math.min(startRow, endRow); row <= Math.max(startRow, endRow); row++) {
                            range.push(col * ROWS + row)
                        }
                    }
                    setSelectedWells(Array.from(new Set([...selectedWells, ...range])))
                }
            } else if (e.ctrlKey || e.metaKey) {
                // Toggle selection
                setSelectedWells((prev) => (prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]))
            } else {
                // Single select
                if (selectedWells.includes(idx)) {
                    // Already selected, deselect
                    setSelectedWells((prev) => prev.filter((i) => i !== idx))
                } else {
                    // Select only this well
                    setSelectedWells([idx])
                }
            }
        },
        [selectedWells, selectByColumn]
    )

    // Delete selected wells
    const handleDelete = useCallback(() => {
        pushUndo()
        setWells((prev) => prev.map((s, i) => (selectedWells.includes(i) ? null : s)))
        setSelectedWells([])
        setUserEdited(true)
    }, [pushUndo, selectedWells])
    // Save plate
    const handleSave = async () => {
        //wells are actually samples | null | {sampleName: string}
        // console.log('wells is',wells)
        const plateSamples = wells
            .map((sample, idx) => {
                if (sample) {
                    return { ...sample, wellidx: idx }
                }
                return null
            })
            .filter((s): s is SampleInterface & { wellidx: number } => s !== null)

        const body = {
            samples: plateSamples,
            type: "96well" as "96well",
        }
        try {
            const res = await savePlate(body)
            if (res.code === 1000) {
                modal.success({ content: "Plate saved successfully!" })
                navigate("/admin/plates")
            } else {
                modal.error({ content: `Error saving plate: ${res.message || "Unknown error"}` })
            }
        } catch (err: any) {
            modal.error({ content: `Error saving plate: ${err.message || "Unknown error"}` })
        }
    }

    // Copy selected wells
    const handleCopy = useCallback(() => {
        setClipboardSamples(selectedWells.map((i) => (wells[i] ? { ...wells[i]! } : null)))
        
        // Detect the pattern of selected wells to preserve it during paste
        if (selectedWells.length <= 1) {
            setClipboardPattern('auto')
            setClipboardShape(null)
        } else {
            const sortedWells = [...selectedWells].sort((a, b) => a - b)
            
            // Convert to coordinates
            const coords = sortedWells.map(idx => ({
                col: Math.floor(idx / ROWS),
                row: idx % ROWS,
                idx
            }))
            
            const minCol = Math.min(...coords.map(c => c.col))
            const maxCol = Math.max(...coords.map(c => c.col))
            const minRow = Math.min(...coords.map(c => c.row))
            const maxRow = Math.max(...coords.map(c => c.row))
            
            const width = maxCol - minCol + 1
            const height = maxRow - minRow + 1
            
            // Check if it's a perfect rectangle (all cells in the bounding box are selected)
            const expectedCells = width * height
            let isRectangle = selectedWells.length === expectedCells
            
            if (isRectangle) {
                // Verify all cells in the rectangle are actually selected
                for (let col = minCol; col <= maxCol; col++) {
                    for (let row = minRow; row <= maxRow; row++) {
                        const idx = col * ROWS + row
                        if (!selectedWells.includes(idx)) {
                            isRectangle = false
                            break
                        }
                    }
                    if (!isRectangle) break
                }
            }
            
            if (isRectangle) {
                if (width === 1) {
                    setClipboardPattern('column')
                    setClipboardShape({ width, height })
                } else if (height === 1) {
                    setClipboardPattern('row')
                    setClipboardShape({ width, height })
                } else {
                    setClipboardPattern('rectangle')
                    setClipboardShape({ width, height })
                }
            } else {
                // Not a perfect rectangle, use auto mode
                setClipboardPattern('auto')
                setClipboardShape(null)
            }
        }
    }, [selectedWells, wells])

    // Paste to selected wells (in order)
    const handlePaste = useCallback(() => {
        if (clipboardSamples.length === 0) return
        pushUndo()
        setWells((prev) => {
            const updated = [...prev]
            
            // If we have selected wells, paste starting from the first selected well
            if (selectedWells.length > 0) {
                const startIdx = Math.min(...selectedWells)
                
                // Convert startIdx to row/col coordinates (using column-major indexing: idx = col * ROWS + row)
                const startCol = Math.floor(startIdx / ROWS)
                const startRow = startIdx % ROWS
                
                if (clipboardPattern === 'rectangle' && clipboardShape) {
                    // Paste in the exact same rectangular shape
                    const { width, height } = clipboardShape
                    let sampleIndex = 0
                    
                    for (let colOffset = 0; colOffset < width; colOffset++) {
                        for (let rowOffset = 0; rowOffset < height; rowOffset++) {
                            const targetCol = startCol + colOffset
                            const targetRow = startRow + rowOffset
                            const targetIdx = targetCol * ROWS + targetRow
                            
                            if (targetCol < COLS && targetRow < ROWS && targetIdx >= 0 && targetIdx < updated.length) {
                                if (sampleIndex < clipboardSamples.length) {
                                    updated[targetIdx] = clipboardSamples[sampleIndex] ? { ...clipboardSamples[sampleIndex]! } : null
                                }
                            }
                            sampleIndex++
                        }
                    }
                } else {
                    // Use existing logic for row, column, or auto patterns
                    let useColumnFill: boolean
                    if (clipboardPattern === 'row') {
                        useColumnFill = false
                    } else if (clipboardPattern === 'column') {
                        useColumnFill = true
                    } else {
                        useColumnFill = fillByColumn // Use current setting for auto/mixed patterns
                    }
                    
                    for (let i = 0; i < clipboardSamples.length; i++) {
                        let targetCol: number
                        let targetRow: number
                        
                        if (useColumnFill) {
                            // Column fill: move down in the same column, then to next column
                            targetCol = startCol + Math.floor(i / ROWS)
                            targetRow = startRow + (i % ROWS)
                        } else {
                            // Row fill: move right in the same row, then to next row
                            targetCol = startCol + (i % COLS)
                            targetRow = startRow + Math.floor(i / COLS)
                        }
                        
                        // Convert back to index and only paste if valid
                        const targetIdx = targetCol * ROWS + targetRow
                        if (targetCol < COLS && targetRow < ROWS && targetIdx >= 0 && targetIdx < updated.length) {
                            updated[targetIdx] = clipboardSamples[i] ? { ...clipboardSamples[i]! } : null
                        }
                    }
                }
            }
            
            return updated
        })
        setUserEdited(true)
    }, [clipboardSamples, selectedWells, pushUndo, fillByColumn, clipboardPattern, clipboardShape])

    const wellidxToLabel = (idx: number): string => {
        const col = Math.floor(idx / ROWS)
        const row = idx % ROWS
        return `${WELL_LABELS[row]}${col + 1}`
    }

    const handleComparePlate = async () => {
        const plateId = parseInt(comparePlateNumber)
        if (isNaN(plateId)) {
            modal.error({ content: "Please enter a valid plate number." })
            return
        }
        setCompareLoading(true)
        try {
            const res = await getPlate({ plateId })
            if (!res.plate) {
                modal.error({ content: `Plate ${plateId} not found.` })
                setCompareLoading(false)
                return
            }
            const otherMap = new Map<string, number>()
            for (const well of res.plate.wells) {
                if (well.sample?.sampleName) {
                    otherMap.set(well.sample.sampleName, well.wellidx)
                }
            }
            const differentPosition: { sampleName: string; currentPos: string; otherPos: string }[] = []
            const notInOther: string[] = []
            wells.forEach((sample, idx) => {
                if (!sample?.sampleName) return
                const name = sample.sampleName
                if (!otherMap.has(name)) {
                    notInOther.push(name)
                } else if (otherMap.get(name) !== idx) {
                    differentPosition.push({
                        sampleName: name,
                        currentPos: wellidxToLabel(idx),
                        otherPos: wellidxToLabel(otherMap.get(name)!),
                    })
                }
            })
            setCompareResult({ differentPosition, notInOther })
        } catch (err: any) {
            modal.error({ content: `Error fetching plate: ${err.message || "Unknown error"}` })
        }
        setCompareLoading(false)
    }

    // Undo handler
    const handleUndo = useCallback(() => {
        setUndoStack((prev) => {
            if (prev.length === 0) return prev
            const last = prev[prev.length - 1]
            setWells(last)
            return prev.slice(0, -1)
        })
    }, [])

    // Keyboard shortcuts for operations (add Ctrl+Z/Cmd+Z for undo)
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            // Prevent global delete/undo/copy/paste when editing an input
            const tag = (e.target as HTMLElement)?.tagName?.toLowerCase()
            if (tag === "input" || tag === "textarea" || (e.target as HTMLElement)?.isContentEditable) {
                return
            }
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "c") {
                handleCopy()
                e.preventDefault()
            }
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "v") {
                handlePaste()
                e.preventDefault()
            }
            if (e.key === "Delete" || e.key === "Backspace") {
                handleDelete()
                e.preventDefault()
            }
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
                handleUndo()
                e.preventDefault()
            }
        }
        window.addEventListener("keydown", handler)
        return () => window.removeEventListener("keydown", handler)
    }, [selectedWells, wells, clipboardSamples, clipboardPattern, clipboardShape, handleUndo, handleCopy, handlePaste, handleDelete])

    const handleEditStart = (idx: number, currentSamplenum: string, currentOrderId?: number) => {
        setEditingWellIdx(idx)
        setEditingValue(currentSamplenum)
        const fallbackOrderId = orders.length === 1 ? orders[0].id : undefined
        setEditingOrderId((currentOrderId ?? fallbackOrderId)?.toString() ?? "")
    }

    if (loading) return <Skeleton />
    if (error) return <div>Error: {error.message || "unknown error"}</div>

    return (
        <DndProvider backend={HTML5Backend}>
            <div style={{ padding: 24 }}>
                <style>
                    {`
                    @media print {
                    body * {
                        visibility: hidden !important;
                    }
                    #plate-print-area, #plate-print-area * {
                        visibility: visible !important;
                    }
                    #plate-print-area {
                        position: fixed !important;
                        left: 0 !important;
                        top: 0 !important;
                        width: 100vw !important;
                        background: white !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        box-shadow: none !important;
                    }
                    #plate-print-area table {
                        width: 100vw !important;
                        font-size: 12pt !important;
                        min-width: 0 !important;
                        max-width: 100vw !important;
                        table-layout: fixed !important;
                    }
                    @page {
                        size: auto;
                        margin: 1.5cm;
                    }
                    }
                    `}
                </style>
                <h2>96-Well Plate Editor</h2>
                <Button type="link" onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>
                    {"<< Go back to last page"}
                </Button>
                <div style={{ marginBottom: 16 }}>
                    <Space size={"large"}>
                        <div>
                            <Switch
                                checked={fillByColumn}
                                onChange={setFillByColumn}
                                checkedChildren="Column Order"
                                unCheckedChildren="Row Order"
                                style={{ marginRight: 8 }}
                            />
                            <span>{fillByColumn ? "Fill by Column" : "Fill by Row"}</span>
                        </div>
                        <div>
                            <Switch
                                checked={selectByColumn}
                                onChange={setSelectByColumn}
                                checkedChildren="Column Order"
                                unCheckedChildren="Row Order"
                                style={{ marginRight: 8 }}
                            />
                            <span>{selectByColumn ? "Select by Column" : "Select by Row"}</span>
                        </div>
                    </Space>
                </div>
                <div style={{ marginBottom: 16 }}>
                    <Button onClick={() => window.print()}>Print</Button>
                    <Button onClick={handleCopy} disabled={selectedWells.length === 0}>
                        Copy
                    </Button>
                    <Button
                        onClick={handlePaste}
                        disabled={clipboardSamples.length === 0 || selectedWells.length === 0}
                    >
                        Paste
                    </Button>
                    <Button onClick={handleDelete} disabled={selectedWells.length === 0}>
                        Delete
                    </Button>
                    <span style={{ marginLeft: 16, color: "#888" }}>
                        (Use Ctrl/Cmd+Click or Shift+Click to select multiple wells)
                    </span>
                </div>
                <div style={{ marginBottom: "1em" }}>
                    Orders: (drag to reorder)
                    {orderIds.map((id, idx) => (
                        <DraggableOrderId key={id} id={id} index={idx} moveOrderId={moveOrderId} />
                    ))}
                </div>
                <div style={{ marginTop: "1em", marginBottom: "1em" }}>
                    <b>Total samples in plate:</b> {wells.filter((w) => w !== null).length}
                </div>
                <div style={{ marginBottom: 16, textAlign: "center", color: "red" }}>
                    {samples.length > 96 && <div>total samples = {samples.length}, more than 96 </div>}
                </div>
                <div id="plate-print-area" style={{ overflowX: "auto" }}>
                    <table style={{ borderCollapse: "collapse", minWidth: 900 }}>
                        <thead>
                            <tr>
                                <th></th>
                                {Array.from({ length: COLS }, (_, i) => (
                                    <th key={i} style={{ border: "1px solid #ccc", padding: 6 }}>
                                        {i + 1}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {WELL_LABELS.map((rowLabel, rowIdx) => (
                                <tr key={rowLabel}>
                                    <td style={{ border: "1px solid #ccc", padding: 6, fontWeight: "bold" }}>
                                        {rowLabel}
                                    </td>
                                    {Array.from({ length: COLS }, (_, colIdx) => {
                                        const idx = colIdx * ROWS + rowIdx
                                        const sample = wells[idx]
                                        const isSelected = selectedWells.includes(idx)
                                        return (
                                            <td
                                                key={colIdx}
                                                tabIndex={0}
                                                style={{
                                                    border: "1px solid #ccc",
                                                    padding: 6,
                                                    background: isSelected ? "#ffd666" : sample ? "#e6f7ff" : "#fafafa",
                                                    minWidth: 80,
                                                    textAlign: "center",
                                                    fontSize: "0.95em",
                                                    height: 40,
                                                    verticalAlign: "middle",
                                                    outline: isSelected ? "2px solid #faad14" : undefined,
                                                    cursor: "pointer",
                                                }}
                                                className="noFocusOutline"
                                                onClick={(e) => handleWellClick(idx, e)}
                                                onKeyDown={(e) => {
                                                    // Move selection with arrows
                                                    // console.log("Key down on well", idx, e.key)

                                                    if (e.key === "ArrowLeft" && colIdx > 0) {
                                                        // console.log("Moving left from", idx)
                                                        // Move all selected wells left if possible and the left neighbor is empty
                                                        pushUndo()
                                                        setWells((prev) => {
                                                            const updated = [...prev]
                                                            if (!selectByColumn) {
                                                                // Row order: push left with row wrapping
                                                                const sorted = [...selectedWells].sort((a, b) => a - b) // leftmost first
                                                                const visited = new Set<number>()
                                                                const newSelected: number[] = []
                                                                
                                                                // Check if movement is possible (find final positions for all cells)
                                                                let canMove = true
                                                                for (const idx of sorted) {
                                                                    let col = Math.floor(idx / ROWS)
                                                                    let row = idx % ROWS
                                                                    let nextCol = col - 1
                                                                    let nextRow = row
                                                                    
                                                                    // If hitting left boundary, wrap to previous row end
                                                                    if (nextCol < 0) {
                                                                        nextCol = COLS - 1
                                                                        nextRow = row - 1
                                                                    }
                                                                    console.log('checking move left for', idx, 'to', nextCol, nextRow)
                                                                    // Only block if we're at the very first position of the plate
                                                                    if (nextRow < 0) {
                                                                        canMove = false
                                                                        break
                                                                    }
                                                                    
                                                                    let checkIdx = nextCol * ROWS + nextRow
                                                                    // Check if there's space or if we can push the chain
                                                                    while (updated[checkIdx] !== null && !visited.has(checkIdx)) {
                                                                        let checkCol = Math.floor(checkIdx / ROWS)
                                                                        let checkRow = checkIdx % ROWS
                                                                        let nextCheckCol = checkCol - 1
                                                                        let nextCheckRow = checkRow
                                                                        
                                                                        if (nextCheckCol < 0) {
                                                                            nextCheckCol = COLS - 1
                                                                            nextCheckRow = checkRow - 1
                                                                        }
                                                                        
                                                                        if (nextCheckRow < 0) {
                                                                            canMove = false
                                                                            break
                                                                        }
                                                                        
                                                                        checkIdx = nextCheckCol * ROWS + nextCheckRow
                                                                    }
                                                                    if (!canMove) break
                                                                }
                                                                
                                                                if (!canMove) return updated

                                                                const pushLeft = (idx: number): number => {
                                                                    if (visited.has(idx)) return idx
                                                                    visited.add(idx)

                                                                    let col = Math.floor(idx / ROWS)
                                                                    let row = idx % ROWS
                                                                    let nextCol = col - 1
                                                                    let nextRow = row
                                                                    
                                                                    // Handle row wrapping
                                                                    if (nextCol < 0) {
                                                                        nextCol = COLS - 1
                                                                        nextRow = row - 1
                                                                    }
                                                                    
                                                                    // Can't move beyond top of plate
                                                                    if (nextRow < 0) return idx
                                                                    
                                                                    let nextIdx = nextCol * ROWS + nextRow

                                                                    if (updated[nextIdx] !== null) {
                                                                        pushLeft(nextIdx)
                                                                    }
                                                                    updated[nextIdx] = updated[idx]
                                                                    updated[idx] = null
                                                                    return nextIdx
                                                                }

                                                                for (const idx of sorted) {
                                                                    if (updated[idx] !== null) {
                                                                        const newIdx = pushLeft(idx)
                                                                        newSelected.push(newIdx)
                                                                    }
                                                                }

                                                                setSelectedWells(newSelected)
                                                                setUserEdited(true)
                                                                return updated
                                                            }

                                                            // Column order: push left, wrapping to the bottom of the previous column if needed
                                                            const sorted = [...selectedWells].sort((a, b) => a - b)
                                                            const visited = new Set<number>()
                                                            let canMove = true
                                                            for (const idx of sorted) {
                                                                let isLeftmost = Math.floor(idx / ROWS) === 0
                                                                let nextIdx: number
                                                                if (isLeftmost) {
                                                                    // Move to previous row
                                                                    const col = Math.floor(idx / ROWS)
                                                                    const row = idx % ROWS
                                                                    if (col >= 0) {
                                                                        nextIdx = (col - 1) * ROWS + row
                                                                    } else {
                                                                        canMove = false
                                                                        break
                                                                    }
                                                                } else {
                                                                    nextIdx = idx - ROWS
                                                                }
                                                                let checkIdx = nextIdx
                                                                while (updated[checkIdx] !== null) {
                                                                    isLeftmost = checkIdx % COLS === 0
                                                                    if (isLeftmost) {
                                                                        const col = Math.floor(checkIdx / ROWS) - 1
                                                                        const row = checkIdx % ROWS
                                                                        if (col >= 0) {
                                                                            checkIdx = col * ROWS + row
                                                                        } else {
                                                                            canMove = false
                                                                            break
                                                                        }
                                                                    } else {
                                                                        checkIdx = checkIdx - ROWS
                                                                    }
                                                                }
                                                                if (!canMove) break
                                                            }
                                                            if (!canMove) return updated

                                                            const newSelected: number[] = []
                                                            const pushLeft = (idx: number): number => {
                                                                if (visited.has(idx)) return idx
                                                                visited.add(idx)

                                                                let col = Math.floor(idx / ROWS)
                                                                let row = idx % ROWS
                                                                let isLeftmost = col === 0
                                                                let nextIdx: number
                                                                if (isLeftmost) {
                                                                    // Can't move further left, stay in place
                                                                    return idx
                                                                } else {
                                                                    nextIdx = (col - 1) * ROWS + row
                                                                }
                                                                if (updated[nextIdx] !== null) {
                                                                    pushLeft(nextIdx)
                                                                }
                                                                updated[nextIdx] = updated[idx]
                                                                updated[idx] = null
                                                                return nextIdx
                                                            }

                                                            for (const idx of sorted) {
                                                                if (updated[idx] !== null) {
                                                                    const newIdx = pushLeft(idx)
                                                                    newSelected.push(newIdx)
                                                                }
                                                            }

                                                            setSelectedWells(newSelected)
                                                            setUserEdited(true)
                                                            return updated
                                                        })
                                                        e.preventDefault()
                                                    }
                                                    if (e.key === "ArrowRight" && colIdx <= COLS - 1) {
                                                        // Move all selected wells right if possible and the right neighbor is empty
                                                        pushUndo()
                                                        setWells((prev) => {
                                                            console.log('pushing right')
                                                            const updated = [...prev]
                                                            if (!selectByColumn) {
                                                                // Row order: push right with row wrapping
                                                                const sorted = [...selectedWells].sort((a, b) => b - a) // rightmost first
                                                                const visited = new Set<number>()
                                                                const newSelected: number[] = []
                                                                
                                                                // Check if movement is possible (find final positions for all cells)
                                                                let canMove = true
                                                                for (const idx of sorted) {
                                                                    let col = Math.floor(idx / ROWS)
                                                                    let row = idx % ROWS
                                                                    let nextCol = col + 1
                                                                    let nextRow = row
                                                                    
                                                                    // If hitting right boundary, wrap to next row
                                                                    if (nextCol >= COLS) {
                                                                        nextCol = 0
                                                                        nextRow = row + 1
                                                                    }
                                                                    console.log("Pushing right from", idx, "to", nextCol, nextRow)
                                                                    // Only block if we're at the very last position of the plate
                                                                    if (nextRow >= ROWS) {
                                                                        canMove = false
                                                                        break
                                                                    }
                                                                    
                                                                    let checkIdx = nextCol * ROWS + nextRow
                                                                    // Check if there's space or if we can push the chain
                                                                    while (updated[checkIdx] !== null && !visited.has(checkIdx)) {
                                                                        let checkCol = Math.floor(checkIdx / ROWS)
                                                                        let checkRow = checkIdx % ROWS
                                                                        let nextCheckCol = checkCol + 1
                                                                        let nextCheckRow = checkRow
                                                                        
                                                                        if (nextCheckCol >= COLS) {
                                                                            nextCheckCol = 0
                                                                            nextCheckRow = checkRow + 1
                                                                        }
                                                                        
                                                                        if (nextCheckRow >= ROWS) {
                                                                            canMove = false
                                                                            break
                                                                        }
                                                                        
                                                                        checkIdx = nextCheckCol * ROWS + nextCheckRow
                                                                    }
                                                                    if (!canMove) break
                                                                }
                                                                
                                                                if (!canMove) return updated

                                                                const pushRight = (idx: number): number => {
                                                                    if (visited.has(idx)) return idx
                                                                    visited.add(idx)

                                                                    let col = Math.floor(idx / ROWS)
                                                                    let row = idx % ROWS
                                                                    let nextCol = col + 1
                                                                    let nextRow = row
                                                                    
                                                                    // Handle row wrapping
                                                                    if (nextCol >= COLS) {
                                                                        nextCol = 0
                                                                        nextRow = row + 1
                                                                    }
                                                                    
                                                                    // Can't move beyond bottom of plate
                                                                    if (nextRow >= ROWS) return idx
                                                                    
                                                                    let nextIdx = nextCol * ROWS + nextRow

                                                                    if (updated[nextIdx] !== null) {
                                                                        pushRight(nextIdx)
                                                                    }
                                                                    updated[nextIdx] = updated[idx]
                                                                    updated[idx] = null
                                                                    return nextIdx
                                                                }

                                                                for (const idx of sorted) {
                                                                    if (updated[idx] !== null) {
                                                                        const newIdx = pushRight(idx)
                                                                        newSelected.push(newIdx)
                                                                    }
                                                                }

                                                                setSelectedWells(newSelected)
                                                                setUserEdited(true)
                                                                return updated
                                                            }
                                                            // Column order: push right, wrapping to the top of the next column if needed
                                                            const sorted = [...selectedWells].sort((a, b) => b - a)
                                                            const visited = new Set<number>()
                                                            let canMove = true
                                                            for (const idx of sorted) {
                                                                let col = Math.floor(idx / ROWS)
                                                                let row = idx % ROWS
                                                                let isRightmost = col === COLS - 1
                                                                let nextIdx: number
                                                                if (isRightmost) {
                                                                    // Can't move further right, stay in place
                                                                    nextIdx=idx
                                                                } else {
                                                                    nextIdx = (col + 1) * ROWS + row
                                                                }
                                                                let checkIdx = nextIdx
                                                                while (updated[checkIdx] !== null) {
                                                                    let col = Math.floor(checkIdx / ROWS)
                                                                    let row = checkIdx % ROWS
                                                                    let isRightmost = col === COLS - 1
                                                                    if (isRightmost) {
                                                                        canMove = false
                                                                        break
                                                                    } else {
                                                                        col += 1
                                                                        checkIdx = col * ROWS + row
                                                                    }
                                                                }
                                                                if (!canMove) break
                                                            }
                                                            if (!canMove) return updated

                                                            const newSelected: number[] = []
                                                            const pushRight = (idx: number): number => {
                                                                if (visited.has(idx)) return idx
                                                                visited.add(idx)

                                                                let col = Math.floor(idx / ROWS)
                                                                let row = idx % ROWS
                                                                let isRightmost = col === COLS - 1
                                                                let nextIdx: number
                                                                if (isRightmost) {
                                                                    // Can't move further right, stay in place
                                                                    return idx
                                                                } else {
                                                                    nextIdx = (col + 1) * ROWS + row
                                                                }
                                                                if (updated[nextIdx] !== null) {
                                                                    pushRight(nextIdx)
                                                                }
                                                                updated[nextIdx] = updated[idx]
                                                                updated[idx] = null
                                                                return nextIdx
                                                            }

                                                            for (const idx of sorted) {
                                                                if (updated[idx] !== null) {
                                                                    const newIdx = pushRight(idx)
                                                                    newSelected.push(newIdx)
                                                                }
                                                            }

                                                            setSelectedWells(newSelected)
                                                            setUserEdited(true)
                                                            return updated
                                                        })
                                                        e.preventDefault()
                                                    }
                                                    if (e.key === "ArrowDown" && rowIdx < ROWS) {
                                                        pushUndo()
                                                        setWells((prev) => {
                                                            let updated = [...prev]
                                                            if (!selectByColumn) {
                                                                // Row order: move down only if the cell below is empty and in the same column
                                                                // Sort selected wells so bottom-most are handled first
                                                                const sorted = [...selectedWells].sort((a, b) => b - a)
                                                                const newSelected: number[] = []
                                                                for (const idx of sorted) {
                                                                    const col = Math.floor(idx / ROWS)
                                                                    const row = idx % ROWS
                                                                    const downRow = row + 1
                                                                    const downIdx = col * ROWS + downRow
                                                                    if (
                                                                        downRow < ROWS &&
                                                                        updated[idx] &&
                                                                        !updated[downIdx]
                                                                    ) {
                                                                        updated[downIdx] = updated[idx]
                                                                        updated[idx] = null
                                                                        newSelected.push(downIdx)
                                                                    } else if (updated[idx]) {
                                                                        newSelected.push(idx)
                                                                    }
                                                                }
                                                                // Update selection after wells update
                                                                setSelectedWells(newSelected)
                                                                setUserEdited(true)
                                                                return updated
                                                            }
                                                            // Sort selected wells so bottom-most are handled last (to avoid overwriting)
                                                            const sorted = [...selectedWells].sort((a, b) => b - a)
                                                            const visited = new Set<number>()
                                                            let canMove = true

                                                            // First, check if any movement would go out of bounds
                                                            for (const idx of sorted) {
                                                                let col = Math.floor(idx / ROWS)
                                                                let row = idx % ROWS
                                                                let isBottom = row === ROWS - 1
                                                                let nextIdx: number
                                                                if (isBottom) {
                                                                    // Move to top of next column if possible
                                                                    let rightCol = col + 1
                                                                    if (rightCol < COLS) {
                                                                        nextIdx = rightCol * ROWS // row 0 of next column
                                                                    } else {
                                                                        canMove = false // Would go out of bounds
                                                                        break
                                                                    }
                                                                } else {
                                                                    nextIdx = col * ROWS + (row + 1)
                                                                }
                                                                // Check recursively if the chain would go out of bounds
                                                                let checkIdx = nextIdx
                                                                while (updated[checkIdx] !== null) {
                                                                    let col = Math.floor(checkIdx / ROWS)
                                                                    let row = checkIdx % ROWS
                                                                    let isBottom = row === ROWS - 1
                                                                    if (isBottom) {
                                                                        col += 1
                                                                        if (col < COLS) {
                                                                            checkIdx = col * ROWS // move to row 0 of next column
                                                                        } else {
                                                                            canMove = false
                                                                            break
                                                                        }
                                                                    } else {
                                                                        checkIdx = col * ROWS + (row + 1)
                                                                    }
                                                                }
                                                                if (!canMove) break
                                                            }

                                                            if (!canMove) return updated // No movement if any would go out of bounds

                                                            // Helper to recursively push a well down
                                                            function pushDown(idx: number) {
                                                                if (visited.has(idx)) return
                                                                visited.add(idx)

                                                                let col = Math.floor(idx / ROWS)
                                                                let row = idx % ROWS
                                                                let isBottom = row === ROWS - 1
                                                                let nextIdx: number
                                                                if (isBottom) {
                                                                    // Move to top of next column if possible
                                                                    let rightCol = col + 1
                                                                    if (rightCol < COLS) {
                                                                        nextIdx = rightCol * ROWS // row 0 of next column
                                                                    } else {
                                                                        return // No more columns, can't move further
                                                                    }
                                                                } else {
                                                                    nextIdx = col * ROWS + (row + 1)
                                                                }
                                                                // If nextIdx is occupied, push it down first
                                                                if (updated[nextIdx] !== null) {
                                                                    pushDown(nextIdx)
                                                                }
                                                                // Move current well down
                                                                updated[nextIdx] = updated[idx]
                                                                updated[idx] = null
                                                            }

                                                            for (const idx of sorted) {
                                                                if (updated[idx] !== null) {
                                                                    pushDown(idx)
                                                                }
                                                            }
                                                            return updated
                                                        })

                                                        // Move selection down as well (only if movement is possible)
                                                        setSelectedWells((prev) => {
                                                            // Use the same canMove logic as above
                                                            if (!selectByColumn) {
                                                                // Row order: move selection down only if the cell below is empty and in the same column
                                                                return prev.map((idx) => {
                                                                    const col = Math.floor(idx / ROWS)
                                                                    const row = idx % ROWS
                                                                    const downRow = row + 1
                                                                    const downIdx = col * ROWS + downRow
                                                                    if (downRow < ROWS && !wells[downIdx]) {
                                                                        return downIdx
                                                                    }
                                                                    return idx
                                                                })
                                                            }
                                                            let canMove = true
                                                            for (const idx of prev) {
                                                                let col = Math.floor(idx / ROWS)
                                                                let row = idx % ROWS
                                                                let isBottom = row === ROWS - 1
                                                                let nextIdx: number
                                                                if (isBottom) {
                                                                    // Move to top of next column if possible
                                                                    let rightCol = col + 1
                                                                    if (rightCol < COLS) {
                                                                        nextIdx = rightCol * ROWS // row 0 of next column
                                                                    } else {
                                                                        canMove = false
                                                                        break
                                                                    }
                                                                } else {
                                                                    nextIdx = col * ROWS + (row + 1)
                                                                }
                                                                // Check recursively if the chain would go out of bounds
                                                                let checkIdx = nextIdx
                                                                while (wells[checkIdx] !== null) {
                                                                    let col = Math.floor(checkIdx / ROWS)
                                                                    let row = checkIdx % ROWS
                                                                    let isBottom = row === ROWS - 1
                                                                    if (isBottom) {
                                                                        col += 1
                                                                        if (col < COLS) {
                                                                            checkIdx = col * ROWS // move to row 0 of next column
                                                                        } else {
                                                                            canMove = false
                                                                            break
                                                                        }
                                                                    } else {
                                                                        checkIdx = col * ROWS + (row + 1)
                                                                    }
                                                                }
                                                                if (!canMove) break
                                                            }
                                                            if (!canMove) return prev
                                                            return prev.map((idx) => {
                                                                const col = Math.floor(idx / ROWS)
                                                                const row = idx % ROWS
                                                                const isBottom = row === ROWS - 1
                                                                if (isBottom) {
                                                                    const rightCol = col + 1
                                                                    if (rightCol < COLS) {
                                                                        return rightCol * ROWS // move to row 0 of next column
                                                                    }
                                                                    return idx // can't move further
                                                                } else {
                                                                    return col * ROWS + (row + 1)
                                                                }
                                                            })
                                                        })
                                                        setUserEdited(true)
                                                        e.preventDefault()
                                                    }
                                                    if (e.key === "ArrowUp" && rowIdx > 0) {
                                                        pushUndo()
                                                        setWells((prev) => {
                                                            let updated = [...prev]
                                                            // console.log('fillByColumn is',fillByColumn)
                                                            // If selectByColumn is false (row order), move up only if the cell above is empty and in the same column
                                                            if (!selectByColumn) {
                                                                // Sort selected wells so top-most are handled first
                                                                const sorted = [...selectedWells].sort((a, b) => a - b)
                                                                const newSelected: number[] = []
                                                                for (const idx of sorted) {
                                                                    const col = Math.floor(idx / ROWS)
                                                                    const row = idx % ROWS
                                                                    const upRow = row - 1
                                                                    const upIdx = col * ROWS + upRow
                                                                    if (upRow >= 0 && updated[idx] && !updated[upIdx]) {
                                                                        updated[upIdx] = updated[idx]
                                                                        updated[idx] = null
                                                                        newSelected.push(upIdx)
                                                                    } else if (updated[idx]) {
                                                                        newSelected.push(idx)
                                                                    }
                                                                }
                                                                // Update selection after wells update
                                                                setSelectedWells(newSelected)
                                                                setUserEdited(true)
                                                                return updated
                                                            }
                                                            // console.log('Moving up in column order')
                                                            // Sort selected wells so top-most are handled first (to avoid overwriting)
                                                            const sorted = [...selectedWells].sort((a, b) => a - b)
                                                            const visited = new Set<number>()
                                                            let canMove = true

                                                            // First, check if any movement would go out of bounds
                                                            for (const idx of sorted) {
                                                                let col = Math.floor(idx / ROWS)
                                                                let row = idx % ROWS
                                                                let isTop = row === 0
                                                                let nextIdx: number
                                                                if (isTop) {
                                                                    // Move to bottom of previous column if possible
                                                                    let leftCol = col - 1
                                                                    if (leftCol >= 0) {
                                                                        nextIdx = leftCol * ROWS + (ROWS - 1)
                                                                    } else {
                                                                        canMove = false // Would go out of bounds
                                                                        break
                                                                    }
                                                                } else {
                                                                    nextIdx = col * ROWS + (row - 1)
                                                                }
                                                                // Check recursively if the chain would go out of bounds
                                                                let checkIdx = nextIdx
                                                                while (updated[checkIdx] !== null) {
                                                                    let checkCol = Math.floor(checkIdx / ROWS)
                                                                    let checkRow = checkIdx % ROWS
                                                                    let isTop = checkRow === 0
                                                                    if (isTop) {
                                                                        let leftCol = checkCol - 1
                                                                        if (leftCol >= 0) {
                                                                            checkIdx = leftCol * ROWS + (ROWS - 1)
                                                                        } else {
                                                                            canMove = false
                                                                            break
                                                                        }
                                                                    } else {
                                                                        checkIdx = checkCol * ROWS + (checkRow - 1)
                                                                    }
                                                                }
                                                                if (!canMove) break
                                                            }

                                                            if (!canMove) return updated // No movement if any would go out of bounds

                                                            // Helper to recursively push a well up
                                                            function pushUp(idx: number) {
                                                                if (visited.has(idx)) return
                                                                visited.add(idx)

                                                                let col = Math.floor(idx / ROWS)
                                                                let row = idx % ROWS
                                                                let isTop = row === 0
                                                                let nextIdx: number
                                                                if (isTop) {
                                                                    // Move to bottom of previous column if possible
                                                                    let leftCol = col - 1
                                                                    if (leftCol >= 0) {
                                                                        nextIdx = leftCol * ROWS + (ROWS - 1)
                                                                    } else {
                                                                        return // No more columns, can't move further
                                                                    }
                                                                } else {
                                                                    nextIdx = col * ROWS + (row - 1)
                                                                }
                                                                // If nextIdx is occupied, push it up first
                                                                if (updated[nextIdx] !== null) {
                                                                    pushUp(nextIdx)
                                                                }
                                                                // Move current well up
                                                                updated[nextIdx] = updated[idx]
                                                                updated[idx] = null
                                                            }

                                                            for (const idx of sorted) {
                                                                if (updated[idx] !== null) {
                                                                    pushUp(idx)
                                                                }
                                                            }
                                                            return updated
                                                        })

                                                        // Move selection up as well (row order: only move if cell above is empty and in the same column)
                                                        setSelectedWells((prev) => {
                                                            if (!selectByColumn) {
                                                                return prev.map((idx) => {
                                                                    const col = Math.floor(idx / ROWS)
                                                                    const row = idx % ROWS
                                                                    const upRow = row - 1
                                                                    const upIdx = col * ROWS + upRow
                                                                    if (upRow >= 0 && !wells[upIdx]) {
                                                                        return upIdx
                                                                    }
                                                                    return idx
                                                                })
                                                            }
                                                            // ...column order selection update as before...
                                                            let canMove = true
                                                            for (const idx of prev) {
                                                                let col = Math.floor(idx / ROWS)
                                                                let row = idx % ROWS
                                                                let isTop = row === 0
                                                                let nextIdx: number
                                                                if (isTop) {
                                                                    // Move to bottom of previous column if possible
                                                                    let leftCol = col - 1
                                                                    if (leftCol >= 0) {
                                                                        nextIdx = leftCol * ROWS + (ROWS - 1)
                                                                    } else {
                                                                        canMove = false
                                                                        break
                                                                    }
                                                                } else {
                                                                    nextIdx = col * ROWS + (row - 1)
                                                                }
                                                                // Check recursively if the chain would go out of bounds
                                                                let checkIdx = nextIdx
                                                                while (wells[checkIdx] !== null) {
                                                                    let checkCol = Math.floor(checkIdx / ROWS)
                                                                    let checkRow = checkIdx % ROWS
                                                                    let isTop = checkRow === 0
                                                                    if (isTop) {
                                                                        let leftCol = checkCol - 1
                                                                        if (leftCol >= 0) {
                                                                            checkIdx = leftCol * ROWS + (ROWS - 1)
                                                                        } else {
                                                                            canMove = false
                                                                            break
                                                                        }
                                                                    } else {
                                                                        checkIdx = checkCol * ROWS + (checkRow - 1)
                                                                    }
                                                                }
                                                                if (!canMove) break
                                                            }
                                                            if (!canMove) return prev
                                                            return prev.map((idx) => {
                                                                const col = Math.floor(idx / ROWS)
                                                                const row = idx % ROWS
                                                                const isTop = row === 0
                                                                if (isTop) {
                                                                    const leftCol = col - 1
                                                                    if (leftCol >= 0) {
                                                                        return leftCol * ROWS + (ROWS - 1)
                                                                    }
                                                                    return idx // can't move further
                                                                } else {
                                                                    return col * ROWS + (row - 1)
                                                                }
                                                            })
                                                        })
                                                        setUserEdited(true)
                                                        e.preventDefault()
                                                    }
                                                    // if (e.key === "ArrowUp" && rowIdx > 0) next = idx - COLS
                                                    // if (e.key === "ArrowDown" && rowIdx < ROWS - 1) next = idx + COLS
                                                    // if (next !== idx) {
                                                    //     setSelectedWells([next])
                                                    //     e.preventDefault()
                                                    // }
                                                    // // Move sample(s) with Shift+Arrow
                                                    // if (e.shiftKey && selectedWells.length > 0) {
                                                    //     handleMove(next)
                                                    //     e.preventDefault()
                                                    // }
                                                }}
                                            >
                                                {editingWellIdx === idx ? (
                                                    <div
                                                        onBlur={(e) => {
                                                            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                                                                handleEditSave(idx)
                                                            }
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") {
                                                                handleEditSave(idx)
                                                            } else if (e.key === "Escape") {
                                                                setEditingWellIdx(null)
                                                                setEditingValue("")
                                                                setEditingOrderId("")
                                                            }
                                                        }}
                                                        style={{ width: "100%", display: "flex", flexDirection: "column", gap: 4 }}
                                                    >
                                                        <input
                                                            type="text"
                                                            value={editingValue}
                                                            autoFocus
                                                            style={{
                                                                width: "90%",
                                                                textAlign: "center",
                                                                fontWeight: 500,
                                                                fontSize: "0.9em",
                                                                margin: "0 auto",
                                                            }}
                                                            placeholder="Samplenum"
                                                            onChange={(e) => setEditingValue(e.target.value)}
                                                        />
                                                        <input
                                                            type="text"
                                                            value={editingOrderId}
                                                            style={{
                                                                width: "90%",
                                                                textAlign: "center",
                                                                fontWeight: 500,
                                                                fontSize: "0.9em",
                                                                margin: "0 auto",
                                                            }}
                                                            placeholder="Order ID"
                                                            onChange={(e) => setEditingOrderId(e.target.value)}
                                                        />
                                                    </div>
                                                ) : sample ? (
                                                    <span
                                                        style={{
                                                            width: "90%",
                                                            border: "none",
                                                            background: "transparent",
                                                            textAlign: "center",
                                                            fontWeight: 500,
                                                            outline: "none",
                                                            cursor: "pointer",
                                                        }}
                                                        onClick={(e) => {
                                                            handleWellClick(idx, e)
                                                            e.stopPropagation()
                                                        }}
                                                        onDoubleClick={() => handleEditStart(idx, sample.samplenum?.toString() ?? "", sample.orderId)}
                                                    >
                                                        {sample.samplenum && <span style={{ fontSize: "8pt", color: "#888" }}>#{sample.samplenum} </span>}
                                                        {sample.sampleName}
                                                        <br />
                                                        <span style={{ fontSize: "8pt" }}>{sample.orderId || ""}</span>
                                                    </span>
                                                ) : (
                                                    <span
                                                        style={{
                                                            width: "90%",
                                                            color: "#bbb",
                                                            cursor: "pointer",
                                                        }}
                                                        onDoubleClick={() => handleEditStart(idx, "", undefined)}
                                                    >
                                                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                                    </span>
                                                )}
                                            </td>
                                        )
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div style={{ marginTop: "1em", marginBottom: "3em" }}>
                    <Button type="primary" onClick={handleSave} style={{ minWidth: 100 }}>
                        Save Plate
                    </Button>
                </div>
                {orders.map((order, idx) => (
                    <div key={order.id} style={{ marginBottom: "1em" }}>
                        <SampleSelector
                            id={order.id ?? 0}
                            editable={!userEdited}
                            orders={orders}
                            setOrders={setOrders}
                        />
                    </div>
                ))}
            </div>

            <div style={{ marginTop: 32, padding: "16px 20px", background: "#f0f7ff", border: "1px solid #91caff", borderRadius: 6, maxWidth: 700 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                    <input
                        type="text"
                        value={comparePlateNumber}
                        onChange={(e) => setComparePlateNumber(e.target.value)}
                        placeholder="Plate number"
                        style={{ width: 120, padding: "4px 8px", borderRadius: 4, border: "1px solid #ccc" }}
                        onKeyDown={(e) => { if (e.key === "Enter") handleComparePlate() }}
                    />
                    <Button onClick={handleComparePlate} loading={compareLoading}>
                        Compare sample position to plate
                    </Button>
                </div>
                {compareResult && (
                    <div style={{ fontSize: "0.9em" }}>
                        {compareResult.differentPosition.length === 0 && compareResult.notInOther.length === 0 && (
                            <div style={{ color: "green" }}>All samples match positions in plate {comparePlateNumber}.</div>
                        )}
                        {compareResult.differentPosition.length > 0 && (
                            <div style={{ marginBottom: 8 }}>
                                <b>Samples at different positions:</b>
                                <ul style={{ margin: "4px 0 0 0", paddingLeft: 20 }}>
                                    {compareResult.differentPosition.map(({ sampleName, currentPos, otherPos }) => (
                                        <li key={sampleName}>
                                            <b>{sampleName}</b>: current = <b>{currentPos}</b>, plate {comparePlateNumber} = <b>{otherPos}</b>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {compareResult.notInOther.length > 0 && (
                            <div>
                                <b>Samples not in plate {comparePlateNumber}:</b>
                                <ul style={{ margin: "4px 0 0 0", paddingLeft: 20 }}>
                                    {compareResult.notInOther.map((name) => (
                                        <li key={name}>{name}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div style={{ marginTop: 32, padding: "16px 20px", background: "#f9f9f9", border: "1px solid #e8e8e8", borderRadius: 6, maxWidth: 700 }}>
                <b style={{ fontSize: "1em" }}>How to edit wells</b>
                <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px", fontSize: "0.9em", color: "#444" }}>
                    <div><b>Selection</b></div>
                    <div></div>
                    <div>Click</div><div>Select a single well (click again to deselect)</div>
                    <div>Shift+Click</div><div>Range select (row or column order based on toggle)</div>
                    <div>Ctrl/Cmd+Click</div><div>Toggle individual wells on/off</div>

                    <div style={{ marginTop: 8 }}><b>Editing a well</b></div>
                    <div></div>
                    <div>Double-click</div><div>Inline-edit the well: enter Order ID and samplenum, then press Enter to save or Escape to cancel</div>
                    <div>Arrow keys</div><div>Move selected samples in that direction, pushing adjacent samples out of the way (wraps between columns in column mode, or between rows in row mode)</div>
                    <div>Delete / Backspace</div><div>Clear samples from all selected wells</div>

                    <div style={{ marginTop: 8 }}><b>Copy &amp; Paste</b></div>
                    <div></div>
                    <div>Ctrl/Cmd+C</div><div>Copy selected wells (preserves shape)</div>
                    <div>Ctrl/Cmd+V</div><div>Paste starting at the first selected well</div>
                    <div>Ctrl/Cmd+Z</div><div>Undo last change</div>

                    <div style={{ marginTop: 8 }}><b>Layout &amp; Order</b></div>
                    <div></div>
                    <div>Fill by Column / Row</div><div>Controls how samples are auto-filled into wells</div>
                    <div>Select by Column / Row</div><div>Controls the direction of Shift+Click range selection</div>
                    <div>Drag order IDs</div><div>Reorder which order's samples come first in the plate</div>
                </div>
            </div>
        </DndProvider>
    )
}
